import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ensureUserCardRows } from "@-/dev.improve.haniverse.lib.cards.user-card.rows";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { eq } from "@-/com.npmjs.drizzle-orm";

export function useUpsertGraphemePronunciationCard() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);

  return useMutation({
    mutationFn: async (input: {
      description: string;
      grapheme: string;
      pinyin: string;
    }): Promise<void> => {
      const { grapheme, pinyin } = input;

      const existing = await actorDb(
        import.meta.location,
      ).query.userGraphemePronunciationCard.findFirst({
        columns: { id: true },
        where: { grapheme, pinyin },
      });
      if (existing === undefined) {
        // A card may only be created for a reading the content catalog has.
        const pronunciationFront = await contentDb(
          import.meta.location,
        ).query.appGraphemePronunciationCard.findFirst({
          where: { grapheme, pinyin },
        });
        if (pronunciationFront === undefined) {
          throw new Error(
            `No pronunciation front row for grapheme '${grapheme}' pinyin '${pinyin}'`,
          );
        }
      }

      await actorDb(import.meta.location).transaction(async (tx) => {
        const { ids } = await ensureUserCardRows(
          tx,
          { grapheme, kind: "pronunciation", pinyin },
          new Date().toISOString(),
        );
        // The mnemonic is an UPDATE of the card's row, never part of the insert that creates it,
        // so it reaches the cloud as a change to this one column.
        await tx
          .update(actorSchema.userGraphemePronunciationCard)
          .set({ description: input.description })
          .where(eq(actorSchema.userGraphemePronunciationCard.id, ids.detailId));
      });
    },
    mutationKey: ["upsertGraphemePronunciationCard"],
  });
}
